import React from 'react';
import PropTypes from 'prop-types';
import deburr from 'lodash/deburr';
import Downshift from 'downshift';
import { withStyles } from '@material-ui/core/styles';
import TextField from '@material-ui/core/TextField';
import Popper from '@material-ui/core/Popper';
import Paper from '@material-ui/core/Paper';
import MenuItem from '@material-ui/core/MenuItem';
import classNames from "classnames";

import customInputStyle from "assets/jss/material-dashboard-pro-react/components/customInputStyle.jsx";
import customDropdownStyle from "assets/jss/material-dashboard-pro-react/components/customDropdownStyle.jsx";
import Loading from '../Loading/Loading';

function renderInput(inputProps) {
    const { InputProps, classes, ref, inputRootCustomClasses, white, error, success, ...other } = inputProps;

    const underlineClasses = classNames({
        [classes.underlineError]: error,
        [classes.underlineSuccess]: success && !error,
        [classes.underline]: true,
        [classes.whiteUnderline]: white
    });
    const inputRoot = classNames({
        [inputRootCustomClasses]: inputRootCustomClasses !== undefined,
        [classes.inputRoot]: true

    });
    const inputClasses = classNames({
        [classes.input]: true,
        [classes.whiteInput]: white,
        [classes.inputInput]: true
    });

    return (
        <TextField
            InputProps={{
                inputRef: ref,
                classes: {
                    root: inputRoot,
                    input: inputClasses,
                    disabled: classes.disabled,
                    underline: underlineClasses
                },
                ...InputProps,
            }}
            {...other}
        />
    );
}

function renderSuggestion({ suggestion, index, itemProps, highlightedIndex, selectedItem, classes, keySelector, labelSelector }) {
    const isHighlighted = highlightedIndex === index;
    const isSelected = selectedItem ? (keySelector(selectedItem) || '').indexOf(keySelector(suggestion)) > -1 : false;

    const dropdownItem = classNames({
        [classes.dropdownItem]: true,
        [classes.roseHover]: true
    });

    return (
        <MenuItem
            {...itemProps}
            key={keySelector(suggestion)}
            selected={isHighlighted}
            component="div"
            className={dropdownItem}
            style={{
                fontWeight: isSelected ? 500 : 400,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis"
            }}
        >
            {labelSelector(suggestion)}
        </MenuItem>
    );
}

renderSuggestion.propTypes = {
    highlightedIndex: PropTypes.number,
    index: PropTypes.number,
    itemProps: PropTypes.object,
    selectedItem: PropTypes.string,
    suggestion: PropTypes.shape({ label: PropTypes.string }).isRequired,
};

// function getSuggestions(value, suggestions) {
//     const inputValue = deburr(value.trim()).toLowerCase();
//     const inputLength = inputValue.length;
//     let count = 0;
//     return inputLength === 0
//         ? []
//         : suggestions.filter(suggestion => {
//             const keep =
//                 count < 5 && suggestion.label.slice(0, inputLength).toLowerCase() === inputValue;
//             if (keep) {
//                 count += 1;
//             }
//             return keep;
//         });
// }

const styles = theme => ({
    root: {
        flexGrow: 1,
        display: 'inline-block'
    },
    container: {
        flexGrow: 1,
        position: 'relative',
    },
    paper: {
        position: 'absolute',
        zIndex: 1,
        marginTop: theme.spacing.unit,
        left: 0,
        right: 0,
    },
    chip: {
        margin: `${theme.spacing.unit / 2}px ${theme.spacing.unit / 4}px`,
    },
    inputRoot: {
        flexWrap: 'wrap',
    },
    inputInput: {
        width: 'auto',
        flexGrow: 1,
    },
    divider: {
        height: theme.spacing.unit * 2,
    },
});

class Autosuggest extends React.Component {

    popperNode;

    state = {
        suggestions: [],
        loading: false
    }

    render() {
        const { onSelect, onChange, classes, placeholder, keySelector, labelSelector, retrieveSuggestions, onOuterClick } = this.props;

        return (
            <div className={classes.root} style={{ display: 'inline-block' }}>
                <Downshift
                    id="downshift-popper"
                    onChange={onChange}
                    onSelect={onSelect}
                    onOuterClick={onOuterClick}
                    onInputValueChange={(inputValue) => this.setState({ loading: true }, () => {
                        this.setState({ inputValue }, () => {
                            retrieveSuggestions(inputValue).then(
                                (suggestions) => {
                                    this.setState({ suggestions, loading: false });
                                }
                            )
                        })
                    })}
                    itemToString={labelSelector}
                >
                    {({
                        getInputProps,
                        getItemProps,
                        getMenuProps,
                        highlightedIndex,
                        inputValue,
                        isOpen,
                        selectedItem
                    }) => (
                            <div className={classes.container}>
                                {renderInput({
                                    fullWidth: true,
                                    classes,
                                    InputProps: getInputProps({
                                        placeholder: placeholder || "Search",
                                    }),
                                    ref: node => {
                                        this.popperNode = node;
                                    },
                                })}
                                <Popper
                                    open={isOpen}
                                    anchorEl={this.popperNode}
                                    className={classNames({
                                        [classes.popperClose]: !isOpen,
                                        [classes.pooperResponsive]: true,
                                        [classes.pooperNav]: isOpen
                                    })}
                                    style={{ zIndex: 1000000 }}
                                >
                                    <div {...(isOpen ? getMenuProps({}, { suppressRefError: true }) : {})}>
                                        <Paper
                                            style={{ marginTop: 8, width: this.popperNode ? this.popperNode.clientWidth : null, zIndex: 1000000 }}
                                            className={classes.dropdown}
                                        >
                                            {
                                                !this.state.loading ?
                                                    this.state.suggestions.map((suggestion, index) =>
                                                        renderSuggestion({
                                                            suggestion,
                                                            index,
                                                            itemProps: getItemProps({ item: suggestion }),
                                                            highlightedIndex,
                                                            selectedItem,
                                                            classes,
                                                            keySelector,
                                                            labelSelector
                                                        })
                                                    )
                                                    :
                                                    <Loading />
                                            }
                                        </Paper>
                                    </div>
                                </Popper>
                            </div>
                        )}
                </Downshift>
            </div>
        );
    }
}

Autosuggest.propTypes = {
    classes: PropTypes.object.isRequired,
    retrieveSuggestions: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    placeholder: PropTypes.string,
    keySelector: PropTypes.func.isRequired,
    labelSelector: PropTypes.func.isRequired
};

export default withStyles({ ...customDropdownStyle, ...customInputStyle, ...styles })(Autosuggest);